import React, { memo, useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import qs from 'querystring';

import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Section } from 'reaxl';
import { clickTypes, DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { inventorySimilarListingsImpression } from 'reaxl-analytics-handlers';
import { InventoryCarousel } from 'reaxl-organisms';

import { saveVDPCarouselListing } from '@/utilities/saveListing';

import { compareListingsDrawerClick } from '@/analytics/inventoryAnalyticsHandlers';
import { inventoryClick, inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';
import vdpSimilarDealerListingsDuck from '@/ducks/vdp/vdpSimilarDealerListingsDuck.js';
import vdpSimilarListingsDuck from '@/ducks/vdp/vdpSimilarListingsDuck.js';

import useSimilarListings from '@/hooks/useSimilarListings';
import useVdpNavigation from '@/hooks/useVdpNavigation';

/**
 * Container component for displaying similar inventory carousels on the Vehicle Detail Page (VDP).
 * Renders either similar inventory across all listings or similar inventory from the same dealer
 * based on the `isSimilarDealerInventoryOnly` prop.
 *
 * @param {boolean} isSimilarDealerInventoryOnly - Flag to indicate whether to show similar inventory from same dealer only - defaults to `false`.
 *
 * @returns The rendered component or `null`
 */
function SimilarInventoryContainer({ isSimilarDealerInventoryOnly = false }) {
    const {
        brand: [, { channel, inventory_image_url: inventoryImageUrl }],
        personalization: [isSaveEnabled, { listings_saved: authenticatedSavesOnly }],
        similar_inventory: [, {
            override_conquestable: overrideConquestableEnabled,
            eligible_for_non_dealer_inventory: eligibleForNonDealerInventory,
        }],
        vdp_enable_second_vehicles_carousel: [isVdpEnableSimiliarVehiclesCarousel],
    } = useFeatures([
        'brand',
        'personalization',
        'similar_inventory',
        'vdp_enable_second_vehicles_carousel',
    ]);

    const dispatch = useDispatch();
    const navigateToVdp = useVdpNavigation();
    const device = useDevice();
    const isXs = device?.is?.xs;

    const similarListingsDuck = isSimilarDealerInventoryOnly
        ? vdpSimilarListingsDuck
        : vdpSimilarDealerListingsDuck;

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const processedListing = overrideConquestableEnabled
        ? { ...listing, isConquestable: false }
        : listing;

    const { isConquestable, owner = {}, priority = '' } = processedListing;
    const isPrivateSeller = owner?.privateSeller || false;

    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);
    const compareListingIds = useSelector(compareListingsDuck.selectors.getActiveListingIds);
    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);
    /**
     * Tracks the visible inventory listings in the carousel on the first load.
     * It contains:
     * - `startIndexInCells` - The index of the first visible item in the carousel.
     * - `ids` - An array of listing IDs for the visible items.
     */
    const [selectedInventories, setSelectedInventories] = useState({});

    const applySecondVehicleCarousel = isVdpEnableSimiliarVehiclesCarousel && !['PREMIUM', 'FEATURED'].includes(priority);
    const isSimilarListingsCarouselEnabled = applySecondVehicleCarousel && eligibleForNonDealerInventory;

    const {
        similarListings: transformedSimilarListings,
        isSimilarListingsInitialLoad,
    } = useSimilarListings({
        listing: processedListing,
        channel,
        isSimilarDealerInventoryOnly,
        isSimilarListingsCarouselEnabled,
        similarListingsDuck,
        inventoryImageUrl,
        isConquestable,
    });

    const handleSendImpressions = useCallback((index, selectedListingsIds, startingIndex) => {
        const cmp = isSimilarDealerInventoryOnly ? 'rv_lstg_' : 'sim_v_lstg_';
        const listingType = isSimilarDealerInventoryOnly ? 'recommendations' : 'similar';

        sendImpressions({
            name: inventoryImpressions,
            data: {
                cmp,
                selectedItemIds: selectedListingsIds,
                pixallData: {
                    impressionType: listingType,
                    label: `${cmp}${index}`,
                    listingType,
                },
                startingIndex,
            },
        });
    }, [isSimilarDealerInventoryOnly]);

    const handleInViewImpression = useCallback(() => {
        const startCellsIndex = selectedInventories?.startIndexInCells;
        const selectedListingsIds = selectedInventories?.ids || [];

        if (!isSimilarDealerInventoryOnly) {
            handleSendImpressions(startCellsIndex, selectedListingsIds, selectedListingsIds.length * startCellsIndex);
        } else if (selectedListingsIds.length >= 0) {
            sendImpressions({
                name: inventorySimilarListingsImpression,
                data: {
                    selectedItemIds: selectedListingsIds,
                },
            });
        }
    }, [handleSendImpressions, isSimilarDealerInventoryOnly, selectedInventories]);

    const handleInventoryListingClick = useCallback(async (event, selectedListing) => {
        // need to stop propagation and prevent default to allow anchors within listing for SEO purposes
        event.stopPropagation();
        event.preventDefault();
        const targetClassName = event && event.target && event.target.className || [];
        const isSaveClick = targetClassName.includes('glyphicon-heart-o');
        const isSelectClick = targetClassName.includes('input-label') && event.target.innerHTML === 'Select';

        if (!(isSaveClick || isSelectClick) && selectedListing) {
            // Wait for sendClick to complete its internal processing
            // This ensures the analytics event is fully processed client-side
            await sendClick(inventoryClick, event, {
                inventoryId: selectedListing.id,
                par: isSimilarDealerInventoryOnly ? 'rv_lstg' : 'sim_v_lstg',
                pixallData: {
                    eventSource: 'vehicle',
                },
            });

            if (selectedListing.id) {
                const urlParams = qs.parse(window.location.search.substring(1));
                delete urlParams.listingId;
                delete urlParams.clickType;

                dispatch(similarListingsDuck.creators.clear());

                // Wait for 100ms to ensure analytics inventoryClick events have enough time to process
                setTimeout(() => {
                    navigateToVdp({
                        ...urlParams,
                        listingId: selectedListing.id,
                        clickType: isSimilarDealerInventoryOnly ? clickTypes.RECOMMENDATIONS : clickTypes.SIMILAR_INVENTORY,
                    });
                }, 100);
            } else {
                return false;
            }
        }

        return true;
    }, [dispatch, isSimilarDealerInventoryOnly, navigateToVdp, similarListingsDuck.creators]);

    const handleSettle = useCallback((index, _direction, _width, _selectedCells, selectedIndices) => {
        const selectedListings = transformedSimilarListings.slice(selectedIndices[0], selectedIndices[selectedIndices.length - 1] + 1);
        const selectedListingsIds = selectedListings.map(({ id }) => id);
        handleSendImpressions(index, selectedListingsIds, selectedIndices[0]);
    }, [handleSendImpressions, transformedSimilarListings]);

    /**
     * Handler called when the carousel is initially rendered.
     * It captures the listings that are visible on the first load to send view impressions for them.
     *
     * @param {number} startIndexInCells - The index of the first cell to be rendered in the viewport.
     * @param {Array} selectedCells - Array containing all inventories in carousel.
     */
    const handleReady = useCallback((startIndexInCells, selectedCells) => {
        const selectedListingIds = transformedSimilarListings
            .slice(startIndexInCells, (startIndexInCells + selectedCells.length))
            .map(({ id }) => id);

        setSelectedInventories({
            startIndexInCells,
            ids: selectedListingIds,
        });
    }, [transformedSimilarListings]);

    const getCarouselHeading = useCallback(() => {
        if (isSimilarDealerInventoryOnly) return 'Check out similar styles in your area';
        if (applySecondVehicleCarousel && !isSimilarDealerInventoryOnly) return 'More to explore from this dealer';
        return `You might also like these similar vehicles${isConquestable ? '' : ' from this dealer'}`;
    }, [isConquestable, applySecondVehicleCarousel, isSimilarDealerInventoryOnly]);

    const showSave = priority !== 'FREE' && (isSaveEnabled || authenticatedSavesOnly);
    const onSaveToggle = useCallback((event, savedListing, wasSaved) => {
        dispatch(saveVDPCarouselListing({
            event,
            listing: savedListing,
            wasSaved,
            sendClick,
            ...(isSimilarDealerInventoryOnly && { parentId: 'rv_lstg' }),
        }));
    }, [dispatch, isSimilarDealerInventoryOnly]);

    const showCompareButton = compareToggle && compareListingIds?.length < 5;
    const isCompared = useCallback((compareListing) => compareListingIds && compareListingIds.includes(compareListing?.id), [compareListingIds]);
    const handleCompareListingsClick = useCallback((compareListing) => async (event) => {
        sendClick(compareListingsDrawerClick, event, {
            clickType: clickTypes.SIMILAR_INVENTORY,
            inventoryId: compareListing?.id,
            inheritPageData: false,
            par: 'comp_v_add',
        });

        dispatch(userPreferencesDuck.creators.setKeys({ showDrawer: true }));
        if (isCompared(compareListing)) {
            dispatch(compareListingsDuck.creators.removeFromCompare(compareListing?.id));
        } else {
            dispatch(compareListingsDuck.creators.addToCompare(compareListing?.id));
        }
    }, [dispatch, isCompared]);

    if (!isPrivateSeller && isSimilarListingsInitialLoad) {
        return (
            <div
                id={isSimilarDealerInventoryOnly ? 'placeholder-similar-inv' : 'placeholder-similar-dealer-inv'}
                style={{ height: isXs ? 336.5 : 369.5 }}
            />
        );
    }

    if (!transformedSimilarListings?.length
        || isPrivateSeller
        || (isSimilarListingsCarouselEnabled && isConquestable && !isSimilarDealerInventoryOnly)) {
        return null;
    }

    const heading = getCarouselHeading();
    const dataCmp = isSimilarDealerInventoryOnly ? 'section-similar-inventory' : 'section-similar-dealer-inventory';

    return (
        <DelayedImpressionObserver
            onChange={handleInViewImpression}
            key={dataCmp}
        >
            <Section
                alignment="left"
                className="margin-top-6"
                data-cmp={dataCmp}
                heading={heading}
                uiContext="level-3"
            >
                <InventoryCarousel
                    orientation={device?.is?.xs ? 'portrait' : 'landscape'}
                    listings={transformedSimilarListings}
                    clickType={isSimilarDealerInventoryOnly ? clickTypes.RECOMMENDATIONS : clickTypes.SIMILAR_INVENTORY}
                    carouselSettings={{
                        fullWidthScroll: true,
                        onReady: handleReady,
                        visibilityThreshold: 0.85,
                    }}
                    owner={owner}
                    onInventoryListingClick={handleInventoryListingClick}
                    onSaveToggle={showSave && onSaveToggle}
                    onSettle={handleSettle}
                    savedListingIds={savedListingIds}
                    showFooter={isConquestable}
                    showCompareButton={showCompareButton}
                    isCompared={isCompared}
                    onCompareClick={handleCompareListingsClick}
                />
            </Section>
        </DelayedImpressionObserver>
    );
}

SimilarInventoryContainer.propTypes = {
    /**
     * Flag to show similar inventory from same dealer only - defaults to false
     */
    isSimilarDealerInventoryOnly: PropTypes.bool,
};

export default memo(SimilarInventoryContainer, _isEqual);
